// OMNI Design System Modal Recipe for Chakra UI v3
// Based on OMNI CSS modal and overlay styles

import { defineSlotRecipe } from '@chakra-ui/react';

export const modalRecipe = defineSlotRecipe({
  className: 'omni-modal',
  slots: [
    'backdrop', // Modal backdrop/overlay
    'content', // Modal content container
    'header', // Modal header
    'title', // Modal title
    'closeButton', // Close button
    'body', // Modal body content
    'footer', // Modal footer
  ],
  base: {
    backdrop: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      zIndex: 'modal',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '4',

      // Animation
      opacity: 0,
      animation: 'fadeIn 200ms ease forwards',

      _open: {
        opacity: 1,
      },
    },

    content: {
      position: 'relative',
      backgroundColor: 'bg.surface',
      borderRadius: 'lg',
      boxShadow: 'modal',
      border: '1px solid',
      borderColor: 'border.default',
      maxWidth: '90vw',
      maxHeight: '90vh',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',

      // Animation
      transform: 'scale(0.95) translateY(-10px)',
      animation: 'slideUp 200ms ease forwards',

      _open: {
        transform: 'scale(1) translateY(0)',
      },

      _focus: {
        outline: 'none',
      },
    },

    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '6',
      borderBottom: '1px solid',
      borderBottomColor: 'border.subtle',
      backgroundColor: 'bg.subtle',
      minHeight: '16',
    },

    title: {
      fontSize: 'xl',
      fontWeight: 'semibold',
      color: 'fg.default',
      lineHeight: 'shorter',
      margin: 0,
      flex: 1,
    },

    closeButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '8',
      height: '8',
      borderRadius: 'sm',
      backgroundColor: 'transparent',
      border: 'none',
      color: 'fg.muted',
      cursor: 'pointer',
      transition: 'all 150ms ease',
      marginLeft: '4',

      _hover: {
        backgroundColor: 'bg.muted',
        color: 'fg.default',
      },

      _active: {
        backgroundColor: 'bg.emphasized',
      },

      _focus: {
        outline: '2px solid',
        outlineColor: 'brand.500',
        outlineOffset: '2px',
      },

      // Close icon
      _before: {
        content: '"×"',
        fontSize: 'xl',
        lineHeight: 1,
      },
    },

    body: {
      flex: 1,
      padding: '6',
      color: 'fg.default',
      fontSize: 'md',
      lineHeight: 'base',
      overflowY: 'auto',
      maxHeight: '60vh',

      // When no header
      _first: {
        paddingTop: '6',
      },

      // When no footer
      _last: {
        paddingBottom: '6',
      },
    },

    footer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      padding: '6',
      borderTop: '1px solid',
      borderTopColor: 'border.subtle',
      backgroundColor: 'bg.subtle',
      gap: '3',
      minHeight: '16',

      // Footer without border (when no body)
      _first: {
        borderTopWidth: 0,
      },
    },
  },

  variants: {
    size: {
      xs: {
        content: {
          width: '20rem',
        },
        header: {
          padding: '4',
          minHeight: '12',
        },
        title: {
          fontSize: 'md',
        },
        body: {
          padding: '4',
          fontSize: 'sm',
        },
        footer: {
          padding: '4',
          minHeight: '12',
          gap: '2',
        },
        closeButton: {
          width: '6',
          height: '6',
          marginLeft: '3',
        },
      },

      sm: {
        content: {
          width: '24rem',
        },
        header: {
          padding: '5',
          minHeight: '14',
        },
        title: {
          fontSize: 'lg',
        },
        body: {
          padding: '5',
          fontSize: 'sm',
        },
        footer: {
          padding: '5',
          minHeight: '14',
          gap: '2.5',
        },
        closeButton: {
          width: '7',
          height: '7',
          marginLeft: '3.5',
        },
      },

      md: {
        content: {
          width: '32rem',
        },
        header: {
          padding: '6',
          minHeight: '16',
        },
        title: {
          fontSize: 'xl',
        },
        body: {
          padding: '6',
          fontSize: 'md',
        },
        footer: {
          padding: '6',
          minHeight: '16',
          gap: '3',
        },
        closeButton: {
          width: '8',
          height: '8',
          marginLeft: '4',
        },
      },

      lg: {
        content: {
          width: '48rem',
        },
        header: {
          padding: '7',
          minHeight: '18',
        },
        title: {
          fontSize: '2xl',
        },
        body: {
          padding: '7',
          fontSize: 'md',
        },
        footer: {
          padding: '7',
          minHeight: '18',
          gap: '3.5',
        },
        closeButton: {
          width: '9',
          height: '9',
          marginLeft: '4.5',
        },
      },

      xl: {
        content: {
          width: '64rem',
        },
        header: {
          padding: '8',
          minHeight: '20',
        },
        title: {
          fontSize: '3xl',
        },
        body: {
          padding: '8',
          fontSize: 'lg',
        },
        footer: {
          padding: '8',
          minHeight: '20',
          gap: '4',
        },
        closeButton: {
          width: '10',
          height: '10',
          marginLeft: '5',
        },
      },

      full: {
        content: {
          width: '100vw',
          height: '100vh',
          maxWidth: '100vw',
          maxHeight: '100vh',
          borderRadius: 'none',
        },
        body: {
          maxHeight: 'calc(100vh - 128px)', // Account for header/footer
        },
      },
    },

    variant: {
      elevated: {
        content: {
          boxShadow: 'modal-elevated',
          border: 'none',
        },
      },

      outline: {
        content: {
          border: '1px solid',
          borderColor: 'border.default',
          boxShadow: 'modal',
        },
      },

      unstyled: {
        backdrop: {
          backgroundColor: 'transparent',
        },
        content: {
          backgroundColor: 'transparent',
          border: 'none',
          boxShadow: 'none',
          borderRadius: 'none',
        },
        header: {
          backgroundColor: 'transparent',
          border: 'none',
          padding: '0',
        },
        body: {
          padding: '0',
        },
        footer: {
          backgroundColor: 'transparent',
          border: 'none',
          padding: '0',
        },
      },
    },

    placement: {
      center: {
        backdrop: {
          alignItems: 'center',
          justifyContent: 'center',
        },
      },

      top: {
        backdrop: {
          alignItems: 'flex-start',
          justifyContent: 'center',
          paddingTop: '16',
        },
      },

      bottom: {
        backdrop: {
          alignItems: 'flex-end',
          justifyContent: 'center',
          paddingBottom: '16',
        },
      },

      left: {
        backdrop: {
          alignItems: 'center',
          justifyContent: 'flex-start',
          paddingLeft: '16',
        },
      },

      right: {
        backdrop: {
          alignItems: 'center',
          justifyContent: 'flex-end',
          paddingRight: '16',
        },
      },
    },

    colorScheme: {
      brand: {
        header: {
          backgroundColor: 'brand.50',
          borderBottomColor: 'brand.100',
        },
        footer: {
          backgroundColor: 'brand.50',
          borderTopColor: 'brand.100',
        },
        content: {
          borderColor: 'brand.200',
        },
      },

      gray: {
        header: {
          backgroundColor: 'gray.50',
          borderBottomColor: 'gray.100',
        },
        footer: {
          backgroundColor: 'gray.50',
          borderTopColor: 'gray.100',
        },
        content: {
          borderColor: 'gray.200',
        },
      },

      success: {
        header: {
          backgroundColor: 'green.50',
          borderBottomColor: 'green.100',
        },
        footer: {
          backgroundColor: 'green.50',
          borderTopColor: 'green.100',
        },
        content: {
          borderColor: 'green.200',
        },
      },

      warning: {
        header: {
          backgroundColor: 'yellow.50',
          borderBottomColor: 'yellow.100',
        },
        footer: {
          backgroundColor: 'yellow.50',
          borderTopColor: 'yellow.100',
        },
        content: {
          borderColor: 'yellow.200',
        },
      },

      error: {
        header: {
          backgroundColor: 'red.50',
          borderBottomColor: 'red.100',
        },
        footer: {
          backgroundColor: 'red.50',
          borderTopColor: 'red.100',
        },
        content: {
          borderColor: 'red.200',
        },
      },
    },

    scrollBehavior: {
      inside: {
        body: {
          overflowY: 'auto',
          maxHeight: '60vh',
        },
      },

      outside: {
        backdrop: {
          overflowY: 'auto',
        },
        content: {
          marginY: '16',
        },
        body: {
          overflowY: 'visible',
          maxHeight: 'none',
        },
      },
    },
  },

  compoundVariants: [
    {
      size: 'full',
      placement: 'center',
      css: {
        backdrop: {
          padding: '0',
        },
      },
    },

    {
      variant: 'elevated',
      colorScheme: 'brand',
      css: {
        content: {
          boxShadow: '0 25px 50px -12px rgba(0, 118, 255, 0.25)',
        },
      },
    },

    {
      scrollBehavior: 'outside',
      size: 'full',
      css: {
        content: {
          marginY: '0',
        },
      },
    },
  ],

  defaultVariants: {
    size: 'md',
    variant: 'elevated',
    placement: 'center',
    colorScheme: 'gray',
    scrollBehavior: 'inside',
  },
});
